import { type FC } from 'react'
import classNames from 'classnames'
import { Col, Form, Row } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { type AccountOpeningHoldersPanel } from '../../../models/AccountOpeningDescriptor'
import { holdersCopy } from '../descriptor/stepCopy'
import {
  asksRelationshipAt,
  defaultNameSource,
  holderSlots,
  relationshipOptionsFor,
  roleKindIn,
  roleLabelIn,
  roleOptions,
  rolesWithHeadroom,
} from '../domain/accountTypeCatalog'
import { HolderPersonPicker } from './HolderPersonPicker'
import { WARNING_BORDER } from '../constants'
import { type ContactOption, type WizardAccount, type WizardHolder } from '../domain/types'

interface AccountHolderFieldsProps {
  accountTypes: AccountType[]
  selectors: AccountOpeningTypeSelector[]
  panel: AccountOpeningHoldersPanel
  account: WizardAccount
  onChange: (account: WizardAccount) => void
  disabled?: boolean
  contactOptions: ContactOption[]
  defaultHolderContactId?: string | null
  accountId: string
}

export const AccountHolderFields: FC<AccountHolderFieldsProps> = ({
  accountTypes,
  selectors,
  panel,
  account,
  onChange,
  disabled = false,
  contactOptions,
  defaultHolderContactId,
  accountId,
}) => {
  const copy = holdersCopy(panel, selectors.length, selectors.map((selector) => selector.label.toLowerCase()).join(' and '))
  const availableRoles = roleOptions(accountTypes, account.typeId)
  const slots = holderSlots(accountTypes, account.typeId)
  const requiredSlotCount = slots.length
  const relationshipOptions = relationshipOptionsFor(accountTypes, account.typeId)
  const rolesForNewHolder = rolesWithHeadroom(accountTypes, account.typeId, account.holders)
  const hasRoleChoice = availableRoles.length > 1
  const launchSlotIndex = slots.findIndex((slot) => slot.kind === 'individual')

  const updateHolder = (holderIndex: number, patch: Partial<WizardHolder>) => {
    onChange({ ...account, holders: account.holders.map((holder, index) => (index === holderIndex ? { ...holder, ...patch } : holder)) })
  }

  const handlePersonChange = (holderIndex: number, contact: ContactOption | null) => {
    if (!contact) {
      updateHolder(holderIndex, { nameSource: null, name: '', contactId: null })
      return
    }
    updateHolder(holderIndex, { nameSource: 'contact', name: contact.name, contactId: contact.id })
  }

  const handleTypeByHand = (holderIndex: number) => updateHolder(holderIndex, { nameSource: 'manual', name: '', contactId: null })

  const addHolder = () => {
    const roleCode = rolesForNewHolder[0]?.value ?? ''
    onChange({
      ...account,
      holders: [
        ...account.holders,
        {
          roleCode,
          nameSource: roleKindIn(accountTypes, roleCode) === 'organization' ? 'manual' : defaultNameSource(contactOptions),
          name: '',
          contactId: null,
        },
      ],
    })
  }

  const removeHolder = (holderIndex: number) => {
    onChange({ ...account, holders: account.holders.filter((_, index) => index !== holderIndex) })
  }

  return (
    <div className='d-flex flex-column zocks-gap-1'>
      <p className='paragraph-medium medium mb-0'>{copy.heading}</p>
      {account.typeId ? (
        <>
          {account.holders.map((holder, holderIndex) => {
            const slot = slots[holderIndex]
            const isEntity = (slot?.kind ?? roleKindIn(accountTypes, holder.roleCode)) === 'organization'
            const lockedToLaunchContact =
              holderIndex === launchSlotIndex && !!defaultHolderContactId && holder.contactId === defaultHolderContactId && holder.name.trim().length > 0
            const removable = holderIndex >= requiredSlotCount && !disabled && !lockedToLaunchContact
            const rolesForThisHolder = rolesWithHeadroom(accountTypes, account.typeId, account.holders, holderIndex)
            const asksRelationship = asksRelationshipAt(accountTypes, account.typeId, holderIndex)
            return (
              <Row key={`${account.accountRefId}-holder-${holderIndex}`} className='g-3'>
                <Col md={hasRoleChoice && asksRelationship ? 4 : hasRoleChoice || asksRelationship ? 6 : true} className='d-flex flex-column zocks-gap-0.5'>
                  <Form.Label className='paragraph-small mb-0'>{slot?.label ?? roleLabelIn(accountTypes, holder.roleCode)}</Form.Label>
                  {isEntity ? (
                    <Form.Control
                      type='text'
                      placeholder={copy.entityNamePlaceholder}
                      value={holder.name}
                      disabled={disabled}
                      className={classNames({ [WARNING_BORDER]: !holder.name.trim() })}
                      onChange={(event) => updateHolder(holderIndex, { name: event.target.value })}
                    />
                  ) : (
                    <>
                      <HolderPersonPicker
                        accountId={accountId}
                        holder={holder}
                        disabled={disabled || lockedToLaunchContact}
                        suggested={contactOptions}
                        onPick={(contact) => handlePersonChange(holderIndex, contact)}
                        onTypeByHand={() => handleTypeByHand(holderIndex)}
                      />
                      {holder.nameSource === 'manual' ? (
                        <Form.Control
                          type='text'
                          placeholder={copy.personNamePlaceholder}
                          value={holder.name}
                          disabled={disabled}
                          className={classNames({ [WARNING_BORDER]: !holder.name.trim() })}
                          onChange={(event) => updateHolder(holderIndex, { name: event.target.value })}
                        />
                      ) : null}
                    </>
                  )}
                </Col>
                {hasRoleChoice ? (
                  <Col md className='d-flex flex-column zocks-gap-0.5'>
                    <Form.Label className='paragraph-small mb-0'>{copy.roleLabel}</Form.Label>
                    <Form.Select value={holder.roleCode} disabled={disabled} onChange={(event) => updateHolder(holderIndex, { roleCode: event.target.value })}>
                      {rolesForThisHolder.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                ) : null}
                {asksRelationship ? (
                  <Col md className='d-flex flex-column zocks-gap-0.5'>
                    <Form.Label className='paragraph-small mb-0'>{copy.relationshipLabel}</Form.Label>
                    <Form.Select
                      value={holder.relationship ?? ''}
                      disabled={disabled}
                      onChange={(event) => updateHolder(holderIndex, { relationship: event.target.value || undefined })}
                    >
                      <option value=''>{copy.relationshipUnset}</option>
                      {relationshipOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                ) : null}
                {removable ? (
                  <Col xs='auto' className='d-flex flex-column zocks-gap-0.5'>
                    <Form.Label className='paragraph-small mb-0'>&nbsp;</Form.Label>
                    <Button variant='secondary' aria-label={copy.removeLabel} onClick={() => removeHolder(holderIndex)}>
                      <i className='bi bi-trash' />
                    </Button>
                  </Col>
                ) : null}
              </Row>
            )
          })}
          {!disabled && rolesForNewHolder.length > 0 ? (
            <Button variant='secondary' className='align-self-start d-flex align-items-center zocks-gap-0.5' onClick={addHolder}>
              <i className='bi bi-plus-circle' />
              {copy.addLabel}
            </Button>
          ) : null}
        </>
      ) : (
        <p className='paragraph-small zocks-text-gray-600 mb-0'>{copy.awaitingType}</p>
      )}
    </div>
  )
}
